/**
 * The firm's desks and the desk phones on them, how its calls ring, and each dispatcher's
 * telephone — as the administrator edits them.
 *
 * Beside the telephony settings rather than inside them: the desks are a list with actions
 * of their own (adding a phone shows its password once), and the rest are separate requests
 * the page's one Save sends alongside the main one.
 */

import { request, type Narrow, type Schema } from '@taxi/api-client';

import type { AnswerOn, DeskPhoneOwnership, WhenOnACall } from '../../telephony/api';

export type DeskPhone = Schema<'DeskPhoneView'>;

/** A desk, who is at it, and its phones in the desk's order. */
export type Desk = Schema<'DeskView'>;

/** How an arriving call is shared out among the desks. */
export type CallDistribution = 'AllAtOnce' | 'TakeTurns' | 'InOrder';

/** When a desk with several phones counts as busy. */
export type DeskBusyWhen = 'AllPhonesBusy' | 'AnyPhoneBusy';

/** A phone set up to fetch its settings, and the address to type into it. */
export type DeskPhoneProvisioning = Schema<'DeskPhoneProvisioning'>;

/**
 * What to type into a desk phone.
 *
 * Returned once, when the phone is added or its password is reset, and never again: the
 * password is written to the PBX and kept nowhere else.
 */
export type DeskPhoneCredentials = Schema<'DeskPhoneCredentials'>;

export type CallHandlingSettings = Narrow<
  Schema<'CallHandlingSettings'>,
  {
    deskPhonesBelongTo: DeskPhoneOwnership;
    callDistribution: CallDistribution;
    deskBusyWhen: DeskBusyWhen;
  }
>;

export type DispatcherTelephone = Narrow<
  Schema<'DispatcherTelephone'>,
  { answerOn: AnswerOn; whenOnACall: WhenOnACall }
>;

export const handlingLimits = {
  ringSeconds: { min: 5, max: 120 },
  holdRecallSeconds: { min: 30, max: 1800 },
  alsoRingOtherAfterSeconds: { min: 3, max: 60 },
  nextDeskAfterSeconds: { min: 3, max: 120 },
};

const base = '/api/tenant/settings/telephony';

export const deskPhonesApi = {
  desks: () => request<Desk[]>(`${base}/desks`),

  addDesk: (name: string) =>
    request<Desk>(`${base}/desks`, { method: 'POST', body: { name } }),

  renameDesk: (id: string, name: string) =>
    request<void>(`${base}/desks/${id}`, { method: 'PUT', body: { name } }),

  /** Refused while the desk has phones on it. */
  removeDesk: (id: string) => request<void>(`${base}/desks/${id}`, { method: 'DELETE' }),

  /** Every desk, once each, first first. */
  reorderDesks: (deskIds: string[]) =>
    request<void>(`${base}/desks/order`, { method: 'PUT', body: { deskIds } }),

  /** Every phone on the desk, once each, first first. */
  reorderPhones: (deskId: string, phoneIds: string[]) =>
    request<void>(`${base}/desks/${deskId}/phone-order`, { method: 'PUT', body: { phoneIds } }),

  /** Gives a desk to a dispatcher, or takes it back with null. */
  assign: (deskId: string, userId: string | null) =>
    request<void>(`${base}/desks/${deskId}/assignee`, { method: 'PUT', body: { userId } }),

  /** Adds a phone to a desk — or, with no desk, a new desk of the same name with it on. */
  add: (name: string, deskId: string | null = null) =>
    request<DeskPhoneCredentials>(`${base}/desk-phones`, {
      method: 'POST',
      body: { name, deskId },
    }),

  /** Moves a phone to another desk, last on it. */
  move: (id: string, deskId: string) =>
    request<void>(`${base}/desk-phones/${id}/desk`, { method: 'PUT', body: { deskId } }),

  rename: (id: string, name: string) =>
    request<DeskPhone>(`${base}/desk-phones/${id}`, { method: 'PUT', body: { name } }),

  resetPassword: (id: string) =>
    request<DeskPhoneCredentials>(`${base}/desk-phones/${id}/password`, { method: 'POST' }),

  remove: (id: string) => request<void>(`${base}/desk-phones/${id}`, { method: 'DELETE' }),

  /** Records the handset's MAC — any separators — or clears it with null. */
  setMac: (id: string, macAddress: string | null) =>
    request<DeskPhoneProvisioning>(`${base}/desk-phones/${id}/mac`, {
      method: 'PUT',
      body: { macAddress },
    }),

  /** Tells the handset to fetch its settings again, now. */
  checkSync: (id: string) =>
    request<void>(`${base}/desk-phones/${id}/check-sync`, { method: 'POST' }),

  provisioning: () => request<Schema<'ProvisioningAddress'>>(`${base}/provisioning`),

  handling: () => request<CallHandlingSettings>(`${base}/handling`),

  saveHandling: (settings: CallHandlingSettings) =>
    request<CallHandlingSettings>(`${base}/handling`, { method: 'PUT', body: settings }),

  dispatcher: (userId: string) =>
    request<DispatcherTelephone>(`${base}/dispatchers/${userId}`),

  saveDispatcher: (userId: string, settings: Omit<DispatcherTelephone, 'deskId'>) =>
    request<DispatcherTelephone>(`${base}/dispatchers/${userId}`, {
      method: 'PUT',
      body: settings,
    }),
};
